import { OpusAgentChatTurnStatus } from "@src/types.generated";
import type { OpusAgentEventLike, OpusAgentPlanProposalLike } from "./events";

// Approval is an ordinary next user message (the scadmin plan-mode
// contract): the Approve button sends exactly this, and the agent also
// takes any typed or spoken yes to the latest plan as approval.
export const APPROVE_PLAN_MESSAGE = "Approved. Build it.";

// The scadmin relay's write tools (opus_agent/relay.py
// _MCP_WRITE_CHIP_TOOLS): a later turn that calls one has started building
// the plan, whatever the message that started it said.
export const PLAN_BUILD_TOOLS: ReadonlySet<string> = new Set([
  "build_course",
  "build_module",
  "build_checkin",
  "build_checklist",
  "build_document",
  "run_graphql_mutation",
  "upload_media",
]);

// needsOk: the chat's latest plan, not yet built. Approve is offered while
//   no turn runs (a question about the plan, answered in text, leaves it
//   open: the agent still waits for an OK).
// approved: the latest plan, followed by the Approve button's message or a
//   turn that called a build tool (a typed or spoken yes).
// replaced: a later plan in the chat supersedes it.
// proposed: the latest plan, not built, in a chat that can't send (an
//   archived or unavailable agent), so nobody can give it an OK.
export type PlanProposalStatus =
  | "needsOk"
  | "proposed"
  | "approved"
  | "replaced";

export interface PlanProposalState {
  eventId: string;
  turnId: string;
  plan: OpusAgentPlanProposalLike;
  status: PlanProposalStatus;
  // Approve is offered: status needsOk and no turn from the plan's on is
  // pending or running.
  actionable: boolean;
  // The version of the plan that replaced this one (status "replaced").
  replacedByVersion: number | null;
}

type TurnLike = {
  id: string;
  userMessage: string;
  status: OpusAgentChatTurnStatus;
  pending?: boolean;
  events: (OpusAgentEventLike & { id: string })[];
};

const startedBuilding = (turn: TurnLike): boolean =>
  turn.userMessage.trim() === APPROVE_PLAN_MESSAGE ||
  turn.events.some((event) => {
    const name = event.payload.toolCall?.toolName;
    return !!name && PLAN_BUILD_TOOLS.has(name);
  });

const isBusy = (turn: TurnLike): boolean =>
  !!turn.pending || turn.status === OpusAgentChatTurnStatus.Running;

/**
 * Every plan in the chat, keyed by event id, with its status.
 *
 * Only the latest plan can need an OK; earlier ones are "replaced by
 * v<next>". The latest is "approved" once a later turn starts building
 * (its message is exactly APPROVE_PLAN_MESSAGE, or it called a build
 * tool); until then it needs an OK, and Approve shows whenever no turn
 * from the plan's on is pending or running.
 */
export const planProposalStates = (
  turns: readonly TurnLike[],
  { canSend = true }: { canSend?: boolean } = {},
): Map<string, PlanProposalState> => {
  const found: {
    eventId: string;
    turnIndex: number;
    plan: OpusAgentPlanProposalLike;
  }[] = [];
  turns.forEach((turn, turnIndex) => {
    for (const event of [...turn.events].sort((a, b) => a.index - b.index)) {
      const plan = event.payload.planProposal;
      if (plan) found.push({ eventId: event.id, turnIndex, plan });
    }
  });
  const states = new Map<string, PlanProposalState>();
  found.forEach(({ eventId, turnIndex, plan }, position) => {
    const turn = turns[turnIndex];
    const next = found[position + 1];
    if (next) {
      states.set(eventId, {
        eventId,
        turnId: turn.id,
        plan,
        status: "replaced",
        actionable: false,
        replacedByVersion: next.plan.version,
      });
      return;
    }
    const later = turns.slice(turnIndex + 1);
    const status: PlanProposalStatus = later.some(startedBuilding)
      ? "approved"
      : canSend
        ? "needsOk"
        : "proposed";
    states.set(eventId, {
      eventId,
      turnId: turn.id,
      plan,
      status,
      actionable: status === "needsOk" && !turns.slice(turnIndex).some(isBusy),
      replacedByVersion: null,
    });
  });
  return states;
};

const KIND_LABEL: Record<string, string> = {
  COURSE: "course",
  MODULE: "module",
  CHECKLIST: "checklist",
  CHECK_IN: "check-in",
  AUDIT: "audit",
  RESOURCE: "resource",
};

// "New course"; null for OTHER or a kind this client predates.
export const planKindHeading = (kind: string): string | null => {
  const label = KIND_LABEL[kind];
  return label ? `New ${label}` : null;
};

type PlanItem = OpusAgentPlanProposalLike["items"][number];

export type PlanItemGroup = {
  // Null for a run of ungrouped items.
  section: string | null;
  // The item's 0-based position in the whole plan, for continuous numbering.
  items: { item: PlanItem; index: number }[];
};

// Consecutive items with the same section form one group; a null (or
// blank) section is ungrouped. The same name later, after another
// section, starts a new group.
export const groupPlanItems = (
  items: OpusAgentPlanProposalLike["items"],
): PlanItemGroup[] => {
  const groups: PlanItemGroup[] = [];
  items.forEach((item, index) => {
    const section = item.section?.trim() || null;
    const last = groups[groups.length - 1];
    if (last && last.section === section) last.items.push({ item, index });
    else groups.push({ section, items: [{ item, index }] });
  });
  return groups;
};

export const planSectionCount = (plan: OpusAgentPlanProposalLike): number =>
  groupPlanItems(plan.items).filter((group) => group.section !== null).length;

// The plan's size for the folded card's subtitle: "2 sections · 5 items",
// or just "5 items" when nothing is grouped.
export const planItemCount = (plan: OpusAgentPlanProposalLike): string => {
  const count = plan.items.length;
  const sections = planSectionCount(plan);
  return [
    sections > 0 && `${sections} ${sections === 1 ? "section" : "sections"}`,
    `${count} ${count === 1 ? "item" : "items"}`,
  ]
    .filter(Boolean)
    .join(" · ");
};

export const planStatusLabel = (state: PlanProposalState): string => {
  if (state.status === "approved") return "Approved";
  if (state.status === "proposed") return "Proposed";
  if (state.status === "replaced")
    return `Replaced by v${state.replacedByVersion}`;
  return "Needs your OK";
};
